import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
const REF_NAME_DIALOG = 'modal-dialog-element';
const clsHeaderFooterCommon = [
    'shrink-0',
    'p-3',
    'bg-neutral-5 dark:bg-neutral-60 text-brand-70 dark:text-brand-2',
];
const ModalHeader = tsx.withPropsObject(tsx.component({
    name: 'cg-modal-header',
    props: {
        modalController: p.ofType().required,
        headerClass: p.ofType().optional,
        showCloseButton: p(Boolean).default(true),
    },
    /** @returns The node. */
    render() {
        return (<header class={[
                clsHeaderFooterCommon,
                'title-medium',
                'rounded-t-lg',
                'flex items-stretch justify-between gap-4',
                this.headerClass,
            ]}>
                    {this.$slots.default}
                    <button data-cy="cg-modal-header-close-button" class="cg-btn btn-neutral-1" onClick={() => {
                this.modalController.close();
            }}>
                        <Icon icon={icons.Close} class="size-5"/>
                        <span class="sr-only">Close modal</span>
                    </button>
                </header>);
    },
}));
const ModalFooter = tsx.withPropsObject(tsx.component({
    name: 'cg-modal-footer',
    props: {
        modalController: p.ofType().required,
        footerClass: p.ofType().optional,
    },
    /** @returns The node. */
    render() {
        return (<footer class={[
                clsHeaderFooterCommon,
                'label-medium',
                'rounded-b-lg',
                this.footerClass,
            ]}>
                    {this.$slots.default}
                </footer>);
    },
}));
/**
 * A component that renders a modal that makes the rest of the page inert and center itself.
 */
export const Modal = tsx.componentFactoryOf().create({
    name: 'cg-modal',
    model: {
        prop: 'isOpen',
        event: 'toggle',
    },
    props: {
        id: p(String).optional,
        /** Useful for component that should fill the entire dialog body. */
        seamless: p(Boolean).default(false),
        backdrop: p(Boolean).default(true),
        clickOutsideToClose: p(Boolean).default(true),
        dialogClasses: p.ofType().optional,
        /** ID of an element that labels the dialog (announced when it opens). */
        ariaLabelledBy: p(String).optional,
        /**
         * Controlled open state. When provided, the parent owns the
         * dialog's visibility — listen to `onToggle` (or use `v-model`)
         * to mirror native dismissal back into the bound state. Leave
         * unset to drive the modal imperatively via the scoped-slot
         * `controller.open()` / `controller.close()`.
         */
        isOpen: p(Boolean).optional,
    },
    /** @returns The initial state of the component. */
    data() {
        return {
            modalId: this.id ?? `${utils.getUniqueId()}`,
            hasOpened: false,
            internalIsOpen: this.isOpen ?? false,
        };
    },
    watch: {
        isOpen: {
            immediate: true,
            /** Mirror the controlled `isOpen` prop into the internal state. */
            handler() {
                if (this.isOpen != null) {
                    this.internalIsOpen = this.isOpen;
                }
            },
        },
    },
    /**
     * Set up the watcher that drives the native `<dialog>` open/close
     * from `internalIsOpen`. This must wait for mount so the dialog ref
     * exists; `getDialog` already awaits the ref but starting the
     * watcher before mount would queue work the parent doesn't expect.
     */
    mounted() {
        this.$watch(() => this.internalIsOpen, (newValue) => {
            this.getDialog(dialog => {
                if (newValue && !dialog.open) {
                    dialog.showModal();
                    this.hasOpened = true;
                }
                else if (!newValue && dialog.open) {
                    dialog.close();
                }
            });
            tsx.emitOn(this, 'onToggle', newValue);
        }, { immediate: true });
    },
    methods: {
        /**
         * Get the dialog element and perform actions with it.
         *
         * @param fn A function that receives the dialog element, will be called with the dialog element.
         * @returns A maybe of the referenced dialog element.
         */
        async getDialog(fn) {
            return (await this.$withRef(REF_NAME_DIALOG, utils.getRefAsHTMLElement))
                .filter(val => val instanceof HTMLDialogElement)
                .ifJust(fn);
        },
        /**
         * Close the dialog if the mouse event indicates that there is a click outside of the dialog.
         *
         * See: https://stackoverflow.com/posts/76201152/revisions.
         *
         * @param event The mouse event.
         */
        closeIfClickOutside(event) {
            if (this.clickOutsideToClose && event.target === event.currentTarget) {
                this.controller.close();
            }
        },
    },
    computed: {
        /** @returns The modal controller. */
        controller() {
            return {
                modalId: this.modalId,
                isOpen: this.internalIsOpen,
                hasOpened: this.hasOpened,
                /** Show the modal and make the rest of the page inert. */
                open: () => {
                    if (!this.internalIsOpen) {
                        this.internalIsOpen = true;
                    }
                },
                /** Close the modal. */
                close: () => {
                    if (this.internalIsOpen) {
                        this.internalIsOpen = false;
                    }
                },
            };
        },
        /** @returns The classes of the dialog. */
        finalDialogClasses() {
            return [
                'rounded-t-lg rounded-b-lg border-0 p-0',
                'bg-white dark:bg-neutral-75',
                'overflow-hidden',
                'open:flex flex-col',
                this.backdrop ? 'backdrop:bg-neutral-60/50' : 'backdrop:appearance-none',
                this.dialogClasses,
            ];
        },
        /** @returns The dialog header if specified. */
        maybeHeaderNode() {
            return utils
                .maybeRenderScopedSlot(this.controller, this.$scopedSlots.modalHeader)
                .filter(() => this.hasOpened)
                .map(rendered => (<ModalHeader modalController={this.controller}>{rendered}</ModalHeader>));
        },
        /** @returns The dialog footer, if specified. */
        maybeFooterNode() {
            return utils
                .maybeRenderScopedSlot(this.controller, this.$scopedSlots.modalFooter)
                .filter(() => this.hasOpened)
                .map(rendered => (<ModalFooter modalController={this.controller}>{rendered}</ModalFooter>));
        },
        /** @returns The dialog body. */
        maybeBodyNode() {
            return utils
                .maybeRenderScopedSlot(this.controller, this.$scopedSlots.modalBody)
                .filter(() => this.hasOpened)
                .map(rendered => (<div class={[
                    'cg-modal-body',
                    'grow',
                    'overflow-auto',
                    { 'p-3!': !this.seamless },
                ]}>
                        {rendered}
                    </div>));
        },
    },
    /** @returns Body of the comp. */
    render() {
        return (<div>
                {utils.renderScopedSlot(this.controller, this.$scopedSlots.trigger, utils.emptyVNode)}
                <dialog onMousedown={evt => {
                this.closeIfClickOutside(evt);
            }} onClose={() => {
                // Older browsers do not support onToggle
                // They support close event instead
                // This is necessary to make sure the component
                // being aware that the dialog is dismissed by
                // methods provided by the browser.
                this.internalIsOpen = false;
                tsx.emitOn(this, 'onClose');
            }} ref={REF_NAME_DIALOG} id={this.modalId} aria-labelledby={this.ariaLabelledBy} class={this.finalDialogClasses}>
                    {Maybe.catMaybes([
                this.maybeHeaderNode,
                this.maybeBodyNode,
                this.maybeFooterNode,
            ])}
                </dialog>
            </div>);
    },
});
